/**
 * Bot-token permission allowlist, grouped by module for the create-bot picker.
 *
 * Mirrors `TOKEN_CONFIG[TokenType.BOT].permission_allowlist` in
 * `server/rapa/core/token_config.py`. Keep the two in sync — the backend rejects any
 * permission not on its allowlist.
 *
 * Each group has an optional module-level `access` permission plus zero or more
 * action permissions. Selecting an action auto-selects the module `access`;
 * deselecting `access` clears the module's actions.
 */

export interface BotPermissionDef {
  codename: string
  label: string
}

export interface BotPermissionGroup {
  module: string
  label: string
  access: BotPermissionDef | null
  permissions: BotPermissionDef[]
}

export const BOT_PERMISSION_GROUPS: BotPermissionGroup[] = [
  {
    module: 'expenses',
    label: 'Expenses',
    access: { codename: 'expenses.access', label: 'Access' },
    permissions: [
      { codename: 'expenses.create', label: 'Create' },
      { codename: 'expenses.edit', label: 'Edit' },
    ],
  },
  {
    module: 'receipts',
    label: 'Receipts',
    access: { codename: 'receipts.access', label: 'Access' },
    permissions: [
      { codename: 'receipts.create', label: 'Create' },
      { codename: 'receipts.edit', label: 'Edit' },
      { codename: 'receipts.generate', label: 'Generate' },
    ],
  },
  {
    module: 'reports',
    label: 'Reports',
    access: { codename: 'reports.access', label: 'Access' },
    permissions: [
      { codename: 'reports.create', label: 'Create' },
      { codename: 'reports.edit', label: 'Edit' },
    ],
  },
  {
    module: 'statements',
    label: 'Statements',
    access: { codename: 'statements.access', label: 'Access' },
    permissions: [
      { codename: 'statements.create', label: 'Create' },
      { codename: 'statements.reconcile', label: 'Reconcile' },
    ],
  },
  {
    module: 'vendors',
    label: 'Vendors',
    access: { codename: 'vendors.access', label: 'Access' },
    permissions: [{ codename: 'vendors.create', label: 'Create' }],
  },
  {
    module: 'expense_searches',
    label: 'Expense Searches',
    access: { codename: 'expense_searches.access', label: 'Access' },
    permissions: [{ codename: 'expense_searches.create', label: 'Create' }],
  },
  {
    module: 'payment_accounts',
    label: 'Payment Accounts',
    access: { codename: 'payment_accounts.access', label: 'Access' },
    permissions: [],
  },
  {
    module: 'reservations',
    label: 'Reservations',
    access: { codename: 'reservations.access', label: 'Access' },
    permissions: [],
  },
  {
    module: 'properties',
    label: 'Properties',
    access: { codename: 'properties.access', label: 'Access' },
    permissions: [],
  },
  {
    module: 'chat',
    label: 'WhatsApp & Chat',
    access: { codename: 'chat.access', label: 'Access' },
    permissions: [
      { codename: 'chat.send', label: 'Send' },
      { codename: 'whatsapp_templates.manage', label: 'Manage templates' },
    ],
  },
  {
    module: 'billing',
    label: 'Billing',
    access: { codename: 'billing.access', label: 'Access' },
    permissions: [{ codename: 'billing.manage', label: 'Manage' }],
  },
]

/** Human-readable label for a permission codename, for permission summaries. */
export function botPermissionLabel(codename: string): string {
  for (const group of BOT_PERMISSION_GROUPS) {
    if (group.access?.codename === codename) return `${group.label}: ${group.access.label}`
    const action = group.permissions.find((p) => p.codename === codename)
    if (action) return `${group.label}: ${action.label}`
  }
  return codename
}
